import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import EwaybillType from "@/enum/EwaybillType";
import WaybillTemplatePrintType from "@/enum/WaybillTemplatePrintType";
import WaybillTemplateType from "@/enum/WaybillTemplateType";
import EwaybillTypeNameMap from "@/map/EwaybillTypeNameMap";
import WaybillTemplateTypeNameMap from "@/map/WaybillTemplateTypeNameMap";
import WaybillPrinter from "../../print/waybill/WaybillPrinter";
import EwaybillDTO from "@/dto/order/EwaybillDTO";
import { Modal } from "antd";
import OrderDTO from "@/dto/order/OrderDTO";
import { PrintableOrder, calcOrderWeight } from "@/services/print/waybill/impl/AbstractEwaybillPrinter";
import { EwaybillReprintResult } from "../type";
import EwaybillAccountStore from "@/store/EwaybillAccountStore";
import WaybillNoTypeCode from "@/dto/ewaybillAccount/waybillNoTypeCode";

/**
 * 快递单模板类型和电子面单类型是否一致
 */
export function isSameWaybillType(ewaybillType: EwaybillType, waybillTemplateType: WaybillTemplateType) {
    switch (waybillTemplateType) {
        case WaybillTemplateType.CAINIAO:
            return ewaybillType === EwaybillType.CAINIAO;
        case WaybillTemplateType.JD:
            return (
                ewaybillType === EwaybillType.JD ||
                ewaybillType === EwaybillType.JD_SHARE ||
                ewaybillType === EwaybillType.JD_AUTH
            );
        case WaybillTemplateType.OFFICIAL:
            return ewaybillType === EwaybillType.OFFICIAL || ewaybillType === EwaybillType.OFFICIAL_SHARE;
        case WaybillTemplateType.PDD:
            return (
                ewaybillType === EwaybillType.PDD ||
                ewaybillType === EwaybillType.PDD_AUTH ||
                ewaybillType === EwaybillType.PDD_AUTH_SHARE ||
                ewaybillType === EwaybillType.PDD_SHARE
            );
        case WaybillTemplateType.TAOBAO_CAINIAO:
            return (
                ewaybillType === EwaybillType.TAOBAO_CAINIAO ||
                ewaybillType === EwaybillType.TAOBAO_CAINIAO_AUTH ||
                ewaybillType === EwaybillType.TAOBAO_CAINIAO_AUTH_SHARE
            );
        case WaybillTemplateType.JDL:
            return ewaybillType === EwaybillType.JDL;
        case WaybillTemplateType.JINRITEMAI:
            return (
                ewaybillType === EwaybillType.JINRITEMAI ||
                ewaybillType === EwaybillType.JINRITEMAI_AUTH ||
                ewaybillType === EwaybillType.JINRITEMAI_AUTH_SHARE ||
                ewaybillType === EwaybillType.JINRITEMAI_SHARE ||
                ewaybillType === EwaybillType.JINRITEMAI_SHOP_AUTH ||
                ewaybillType === EwaybillType.JINRITEMAI_IOP ||
                ewaybillType === EwaybillType.JINRITEMAI_SUPPLIER
            );
        case WaybillTemplateType.KWAISHOP:
            return (
                ewaybillType === EwaybillType.KWAISHOP ||
                ewaybillType === EwaybillType.KWAISHOP_SHARE ||
                ewaybillType === EwaybillType.KWAISHOP_SHOP_AUTH ||
                ewaybillType === EwaybillType.KWAISHOP_DROPSHIP
            );

        case WaybillTemplateType.WEBUY:
            return ewaybillType === EwaybillType.WEBUY;

        case WaybillTemplateType.CHANNEL_SHOP:
            return (
                ewaybillType === EwaybillType.CHANNEL_SHOP ||
                ewaybillType === EwaybillType.CHANNEL_SHOP_AUTH ||
                ewaybillType === EwaybillType.CHANNEL_SHOP_SHARE ||
                ewaybillType === EwaybillType.CHANNEL_SHOP_DROPSHIP
            );

        case WaybillTemplateType.XIAOHONGSHU:
            return (
                ewaybillType === EwaybillType.XIAOHONGSHU ||
                ewaybillType === EwaybillType.XIAOHONGSHU_AUTH ||
                ewaybillType === EwaybillType.XIAOHONGSHU_SHARE
            );
        case WaybillTemplateType.AIKUCUN:
            return ewaybillType === EwaybillType.AIKUCUN;
        default:
            return false;
    }
}

async function validateEwaybills(
    ewaybillNos: EwaybillDTO[] = [],
    template: WaybillTemplateDTO,
    ewaybillAccountStore: EwaybillAccountStore,
) {
    const waybillNos: any = [];
    for (let i = 0; i < ewaybillNos.length; i++) {
        const ewaybillNo = ewaybillNos[i];
        if (ewaybillNo.logisticsCompany !== template.logisticsCompany) {
            return "所选电子面单号的物流公司与模板的物流公司不一致，请重新选择！";
        }
        if (!isSameWaybillType(ewaybillNo.type, template.type)) {
            const ewaybillTypeName = EwaybillTypeNameMap[ewaybillNo.type];
            const waybillTemplateTypeName = WaybillTemplateTypeNameMap[template.type];
            return `所选电子面单号类型【${ewaybillTypeName}】的与模板类型【${waybillTemplateTypeName}】不一致，请重新选择！`;
        }
        if (waybillNos.includes(ewaybillNo.waybillNo)) {
            return "您选择的批量重打电子面单号中勾选了重复的运单号，请重新选择！";
        }
        if (template.type === WaybillTemplateType.XIAOHONGSHU) {
            const branchAccount = ewaybillAccountStore.branchAccountMapping.get(
                ewaybillNo.details?.ewaybillShippingAddressId,
            );

            if (branchAccount) {
                if (template.templateVersion === 2) {
                    if (branchAccount.waybillNoTypeCode !== WaybillNoTypeCode.XIAOHONGSHU_V2) {
                        return `所选电子面单号类型为旧版小红书电子面单但模板是新小红书模板，请重新选择！`;
                    }
                } else {
                    if (branchAccount.waybillNoTypeCode === WaybillNoTypeCode.XIAOHONGSHU_V2) {
                        return `所选电子面单号类型为新版小红书电子面单但模板是旧小红书模板，请重新选择！`;
                    }
                }
            }
        }

        waybillNos.push(ewaybillNo.waybillNo);
    }

    /** @see https://jirr20240624073410550.pingcode.com/pjm/workitems/ecI28a1C */
    if (template.type === WaybillTemplateType.JINRITEMAI) {
        const isSameEwaybillShippingAddressId = ewaybillNos.every(
            ({ details }) => details?.ewaybillShippingAddressId === template.ewaybillShippingAddressId,
        );

        if (!isSameEwaybillShippingAddressId) {
            await new Promise((yes, no) => {
                Modal.confirm({
                    okText: "继续打印",
                    content:
                        "所选模板的电子面单账号与快递单号的抖店电子面单账号不一致，会导致无法解密该打印报文，建议重新选择正确的模板",
                    onOk() {
                        yes(null);
                    },
                    onCancel() {
                        no("用户取消操作");
                    },
                });
            });
        }
    }
}

export async function checkShouldPrint<E = EwaybillDTO>(
    waybillTemplate: WaybillTemplateDTO,
    shippingAddress: ShippingAddressDTO,
    ewaybills: E[],
    isPreview: boolean,
    isFromReprint: boolean,
    ewaybillAccountStore: EwaybillAccountStore,
) {
    if (!waybillTemplate) return "你必须选择快递单模板";
    if (!shippingAddress) return "你必须选择发货人信息";
    if (!ewaybills?.length) return "你必须选择一个电子面单";
    if (isPreview && ewaybills.length > 1) {
        if (isFromReprint) {
            return "预览一次只能选择一个运单号";
        } else {
            return "查看底单一次只能选择一个运单号";
        }
    }

    const sureEwaybills = ewaybills as EwaybillDTO[];
    const error = await validateEwaybills(sureEwaybills, waybillTemplate, ewaybillAccountStore);

    if (error) return error;

    if (!WaybillPrinter.isReady(waybillTemplate)) {
        return "打印机未连接";
    }

    if (import.meta.env.VITE_IS_QN && waybillTemplate.printType === WaybillTemplatePrintType.SF) {
        return "请到网页版发货易(https://v2.fahuoyi.com)使用顺丰打印组件打印";
    }

    return undefined;
}

function base64ToBlob(base64Data: string) {
    let arr = base64Data.split(","),
        fileType = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        l = bstr.length,
        u8Arr = new Uint8Array(l);

    while (l--) {
        u8Arr[l] = bstr.charCodeAt(l);
    }

    return new Blob([u8Arr], {
        type: fileType,
    });
}

export function buildBase64Image(images: string[]) {
    const urls = [];

    images.forEach((image) => {
        const blob = base64ToBlob(image);

        const url = URL.createObjectURL(blob);

        urls.push(url);
    });

    return urls;
}

export function showReprintCheckFailTip(waybillTemplate: WaybillTemplateDTO, message: string) {
    if (message === "打印机未连接") {
        WaybillPrinter.openDownloadPluginDialog(waybillTemplate);
    } else {
        Modal.error({ title: "打印失败", content: message });
    }
}

/** @todo 这段逻辑聚合到各个平台PrinterAgent中 */
export function getReprintPrinterName(isPreview: boolean, printer: string, template: WaybillTemplateDTO) {
    const printType = template.printType;

    let printerName = isPreview ? "" : printer;

    if (isPreview) {
        if (
            [WaybillTemplatePrintType.PDD, WaybillTemplatePrintType.KWAISHOP].includes(printType) ||
            (WaybillTemplatePrintType.XIAOHONGSHU === printType && template.templateVersion === 2)
        ) {
            printerName = printer;
        }
    }

    return printerName;
}

// 避免ewaybillNo.id覆盖order.id，因此在最后加{id: order.id}
export function createPrintableOrder(order: OrderDTO, printData: string, ewaybill: EwaybillDTO): PrintableOrder {
    const orderWeight = calcOrderWeight(order);

    return {
        order,
        printData,
        waybillNo: ewaybill.waybillNo,
        orderWeight: Number(orderWeight),
        splitWeight: 0,
        ewaybillInfo: {
            id: ewaybill.id,
            packageIndex: 0,
            packageTotal: 1,
            batchNo: ewaybill.printBatchNo,
        },
    };
}

export function buildPreviewResult<E>(
    waybillTemplate: WaybillTemplateDTO,
    ewaybill: E,
    data,
): EwaybillReprintResult<E> {
    if (waybillTemplate.printType === WaybillTemplatePrintType.SF) {
        if (data === null) {
            /** 顺丰组件内部实现预览 */
            return {
                successful: true,
                ewaybill,
            };
        }
    } else if (typeof data === "object" && data !== null) {
        /** PDD预览url存放在previewURL CAINIAO JD预览URl存放在previewImage */
        const image = data.previewImage || data.previewURL || data.previewUrl;

        if (image) {
            const images: string[] = Array.isArray(image) ? image : [image];
            const urls = waybillTemplate.printType === WaybillTemplatePrintType.JDL ? buildBase64Image(images) : images;

            return {
                successful: true,
                urls,
                ewaybill,
            };
        }
    }

    return {
        successful: false,
        message: typeof data === "string" ? data : data?.message,
        ewaybill,
    };
}
